/**
 * Shared layout helpers used by SettingsSheet section components.
 */

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ChevronRight } from 'lucide-react';
import type { TimeFormat } from '../types';

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function formatHour(hour: number, format: TimeFormat): string {
  if (format === '24h') return `${String(hour).padStart(2, '0')}:00`;
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

export const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function Section({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group border rounded-lg">
      <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-90 shrink-0" />
        <Icon className="h-4 w-4 text-primary shrink-0" />
        <h3 className="text-[13px] font-semibold tracking-tight flex-1">{title}</h3>
      </summary>
      <div className="px-3 pb-3 pt-1">
        {description && (
          <p className="text-[11px] text-muted-foreground mb-3 leading-snug">{description}</p>
        )}
        <div className="space-y-3">{children}</div>
      </div>
    </details>
  );
}

/** A bordered row for a toggle switch -- keeps the switch visually tied to its label. */
export function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2.5">
      <div className="min-w-0">
        <span className="text-[13px] leading-none">{label}</span>
        {description && (
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{description}</p>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="shrink-0" />
    </div>
  );
}

/** Label + control group for selects/inputs. */
export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-[13px] mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
